import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useVisits } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { useUsers } from "../../users/hooks";
import { formatDisplayDate } from "../../../helpers/date";
import { PrimaryContactCell } from "../../contacts/components";
import { cleanObject } from "../../../helpers/clean-object";
import { VisitOutcome } from "../../../__generated__/globalTypes";
import { visitOutcomeLabel } from "../../../helpers/enum-labels";

interface ISearchForm {
  search?: string;
  outcome?: string;
  customerId?: string;
  salesRepId?: string;
}

export const Visits: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      outcome: searchParams.get("outcome") ?? "",
      customerId: searchParams.get("customerId") ?? "",
      salesRepId: searchParams.get("salesRepId") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/visits",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const outcome = searchParams.get("outcome");
  const customerId = searchParams.get("customerId");
  const salesRepId = searchParams.get("salesRepId");
  if (search) where.search = search;
  if (outcome) where.outcome = outcome;
  if (customerId) where.customerId = +customerId;
  if (salesRepId) where.salesRepId = +salesRepId;

  const { data, loading } = useVisits({ where });

  return (
    <>
      <Header
        title="Visites"
        subtitle="Liste des visites commerciales"
        buttons={[
          {
            actionText: "Nouvelle visite",
            bgColor: "indigo",
            textColor: "white",
            link: `/visit/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher une visite..."
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Commercial" className="w-full" {...form.register("salesRepId")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
              <Select label="Résultat" className="w-full" {...form.register("outcome")}>
                <option value="">Tous</option>
                {Object.keys(VisitOutcome).map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {visitOutcomeLabel(outcome)}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.visits?.results?.length ? (
            <EmptyList text="Aucune visite" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Date</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header>Contact</TableCell>
                  <TableCell header>Résultat</TableCell>
                  <TableCell header>Relance</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.visits?.results?.map((visit) => (
                  <TableRow
                    key={`visit-${visit.id}`}
                    onClick={() => navigate(`/visit/${visit.id}`)}
                  >
                    <TableCell>{formatDisplayDate(visit.visitDate)}</TableCell>
                    <TableCell>{visit.customer?.name}</TableCell>
                    <TableCell>
                      {visit.customer?.id && (
                        <PrimaryContactCell customerId={visit.customer.id} />
                      )}
                    </TableCell>
                    <TableCell>{visitOutcomeLabel(visit.outcome)}</TableCell>
                    <TableCell>{formatDisplayDate(visit.followUpDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
